<div class="community-admin container-xl py-3">
  <header class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
    <div>
      <h1 class="h3 mb-0">{{ 'community.admin.title' | translate }}</h1>
      <p class="text-muted small mb-0">{{ 'community.admin.subtitle' | translate }}</p>
    </div>
    <a class="btn btn-outline-secondary btn-sm" [routerLink]="communityLink()">
      ← {{ 'community.admin.back' | translate }}
    </a>
  </header>

  @if (loading() && !structure()) {
    <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
  } @else if (forbidden()) {
    <div class="alert alert-warning">{{ 'community.admin.forbidden' | translate }}</div>
  } @else if (structure(); as st) {
    @if (error(); as key) {
      <div class="alert alert-danger d-flex justify-content-between align-items-start">
        <span>
          {{ key | translate }}
          @if (errorDetail(); as detail) {
            <br /><small>{{ detail }}</small>
          }
        </span>
        <button type="button" class="btn-close" (click)="clearError()"></button>
      </div>
    }

    @if (!canManageRoles()) {
      <div class="alert alert-info py-2 small">{{ 'community.admin.area_admin_notice' | translate }}</div>
    }

    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'structure'" (click)="selectTab('structure')">
          {{ 'community.admin.tabs.structure' | translate }}
        </button>
      </li>
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'rights'" (click)="selectTab('rights')">
          {{ 'community.admin.tabs.rights' | translate }}
        </button>
      </li>
      @if (canManageRoles()) {
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="tab() === 'roles'" (click)="selectTab('roles')">
            {{ 'community.admin.tabs.roles' | translate }}
          </button>
        </li>
      }
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'grants'" (click)="selectTab('grants')">
          {{ 'community.admin.tabs.grants' | translate }}
        </button>
      </li>
      @if (canManageRoles()) {
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="tab() === 'communication'" (click)="selectTab('communication')">
            {{ 'community.admin.tabs.communication' | translate }}
          </button>
        </li>
      }
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'stats'" (click)="selectTab('stats')">
          {{ 'community.admin.tabs.stats' | translate }}
        </button>
      </li>
      @if (canManageRoles()) {
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="tab() === 'webhooks'" (click)="selectTab('webhooks')">
            {{ 'community.admin.tabs.webhooks' | translate }}
          </button>
        </li>
      }
    </ul>

    <!-- ================================================================== -->
    <!-- Groups & channels                                                   -->
    <!-- ================================================================== -->
    @if (tab() === 'structure') {
      <div class="d-flex justify-content-between align-items-center mb-2">
        <p class="text-muted small mb-0">{{ 'community.admin.group.hint' | translate }}</p>
        @if (canManageRoles() && groupFormTarget() === null) {
          <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="openGroupForm()">
            + {{ 'community.admin.group.add' | translate }}
          </button>
        }
      </div>

      @if (groupFormTarget() === 'new') {
        <div class="card mb-3">
          <div class="card-body">
            <h2 class="h6">{{ 'community.admin.group.add' | translate }}</h2>
            <ng-container *ngTemplateOutlet="groupForm"></ng-container>
          </div>
        </div>
      }

      @if (groups().length === 0) {
        <p class="text-muted">{{ 'community.admin.group.empty' | translate }}</p>
      }

      @for (group of groups(); track group.id; let gi = $index) {
        <section class="card mb-3 community-admin__group" [class.community-admin__group--archived]="group.archived">
          <div class="card-header d-flex align-items-center flex-wrap gap-2">
            <span class="fw-semibold">
              {{ group.name }}
              <small class="text-muted ms-1">{{ group.key }}</small>
            </span>
            <span class="badge bg-light text-dark">{{ 'community.admin.group_visibility.' + group.visibility | translate }}</span>
            @for (key of group.role_keys; track key) {
              @if (roleByKey(key); as role) {
                <span [class]="badgeClass(role.badge_color)" [style.background-color]="badgeStyle(role.badge_color)">{{ role.badge_label || role.name }}</span>
              } @else {
                <span class="badge bg-secondary">{{ key }}</span>
              }
            }
            @if (group.system) {
              <span class="badge bg-dark">{{ 'community.admin.common.system' | translate }}</span>
            }
            @if (group.archived) {
              <span class="badge bg-secondary">{{ 'community.admin.common.archived' | translate }}</span>
            }
            <span class="ms-auto d-flex flex-wrap gap-1">
              @if (canManageRoles()) {
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy() || gi === 0" [title]="'community.admin.common.up' | translate" (click)="moveGroup(group, -1)">↑</button>
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy() || gi === groups().length - 1" [title]="'community.admin.common.down' | translate" (click)="moveGroup(group, 1)">↓</button>
              }
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="openGroupForm(group)">
                {{ 'community.admin.common.edit' | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-dark" [disabled]="busy()" (click)="toggleGroupArchived(group)">
                {{ (group.archived ? 'community.admin.common.unarchive' : 'community.admin.common.archive') | translate }}
              </button>
              @if (canManageRoles() && !group.system) {
                <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="deleteGroup(group)">
                  {{ 'community.admin.common.delete' | translate }}
                </button>
              }
            </span>
          </div>

          @if (group.description) {
            <div class="card-body py-2 small text-muted border-bottom">{{ group.description }}</div>
          }

          @if (groupFormTarget() === group.id) {
            <div class="card-body border-bottom">
              <ng-container *ngTemplateOutlet="groupForm"></ng-container>
            </div>
          }

          <ul class="list-group list-group-flush">
            @for (ch of group.channels; track ch.id; let ci = $index) {
              <li class="list-group-item" [class.community-admin__channel--archived]="ch.archived">
                @if (channelFormTarget() === ch.id) {
                  <ng-container *ngTemplateOutlet="channelForm"></ng-container>
                } @else {
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <span class="flex-grow-1">
                      # {{ ch.name }}
                      <small class="text-muted ms-1">{{ ch.key }}</small>
                      <span class="badge bg-info text-dark ms-1">{{ 'community.channel.mode.' + ch.mode | translate }}</span>
                      @if (ch.private) {
                        <span class="badge bg-dark ms-1">🔐 {{ 'community.private_room.badge' | translate }}</span>
                        @if (ch.participants_read_only) {
                          <span class="badge bg-secondary ms-1">{{ 'community.private_room.read_only' | translate }}</span>
                        }
                      }
                      @if (siteName(ch.config_domain_id); as site) {
                        <span class="badge bg-light text-dark border ms-1">{{ site }}</span>
                      }
                      @if (ch.read_only) {
                        <span class="badge bg-secondary ms-1">{{ 'community.channel.read_only' | translate }}</span>
                      }
                      @if (ch.requires_approval) {
                        <span class="badge bg-warning text-dark ms-1">{{ 'community.admin.channel.requires_approval_badge' | translate }}</span>
                      }
                      @if (ch.legacy) {
                        <span class="badge bg-secondary ms-1">{{ 'community.channel.archive' | translate }}</span>
                      }
                      @if (ch.archived) {
                        <span class="badge bg-dark ms-1">{{ 'community.admin.common.archived' | translate }}</span>
                      }
                      @if (ch.system) {
                        <small class="text-muted ms-1">({{ 'community.admin.common.system' | translate }})</small>
                      }
                      <span class="badge bg-light text-dark ms-1" [title]="'community.admin.channel.threads' | translate">{{ ch.thread_count }}</span>
                      @if (ch.links.length) {
                        <span class="badge bg-light text-dark ms-1" [title]="'community.links.title' | translate">📌 {{ ch.links.length }}</span>
                      }
                    </span>
                    <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy() || ci === 0" (click)="moveChannel(group, ch, -1)">↑</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy() || ci === group.channels.length - 1" (click)="moveChannel(group, ch, 1)">↓</button>
                    @if (ch.private) {
                      <button type="button" class="btn btn-sm"
                              [class.btn-dark]="participantsChannelId() === ch.id"
                              [class.btn-outline-dark]="participantsChannelId() !== ch.id"
                              [attr.aria-expanded]="participantsChannelId() === ch.id"
                              (click)="toggleParticipants(ch)">
                        🔐 {{ 'community.admin.private_room.participants' | translate }}
                      </button>
                    }
                    <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="openChannelForm(group, ch)">
                      {{ 'community.admin.common.edit' | translate }}
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-dark" [disabled]="busy()" (click)="toggleChannelArchived(ch)">
                      {{ (ch.archived ? 'community.admin.common.unarchive' : 'community.admin.common.archive') | translate }}
                    </button>
                    @if (!ch.system) {
                      <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="deleteChannel(ch)">
                        {{ 'community.admin.common.delete' | translate }}
                      </button>
                    }
                  </div>
                  @if (ch.description) {
                    <div class="small text-muted mt-1">{{ ch.description }}</div>
                  }

                  @if (ch.private && participantsChannelId() === ch.id) {
                    <ng-container *ngTemplateOutlet="participantsPanel; context: { $implicit: ch }"></ng-container>
                  }

                  @if (deletePendingChannel()?.id === ch.id) {
                    <div class="alert alert-warning mt-2 mb-0">
                      <p class="mb-2">{{ 'community.admin.channel.move_required' | translate }}</p>
                      <div class="d-flex flex-wrap gap-2 align-items-center">
                        <label class="small mb-0">{{ 'community.admin.channel.move_target' | translate }}</label>
                        <select class="form-select form-select-sm w-auto" [(ngModel)]="deleteMoveTargetId">
                          @for (target of deleteMoveTargets(); track target.channel.id) {
                            <option [ngValue]="target.channel.id">{{ target.group.name }} / # {{ target.channel.name }}</option>
                          }
                        </select>
                        <button type="button" class="btn btn-sm btn-danger" [disabled]="busy() || deleteMoveTargetId == null" (click)="confirmDeleteWithMove()">
                          {{ 'community.admin.channel.move_and_delete' | translate }}
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelDeleteWithMove()">
                          {{ 'community.admin.common.cancel' | translate }}
                        </button>
                      </div>
                    </div>
                  }
                }
              </li>
            } @empty {
              <li class="list-group-item text-muted small">{{ 'community.admin.channel.empty' | translate }}</li>
            }

            @if (channelFormTarget() === 'new' && channelFormGroupId() === group.id) {
              <li class="list-group-item">
                <h3 class="h6">{{ 'community.admin.channel.add' | translate }}</h3>
                <ng-container *ngTemplateOutlet="channelForm"></ng-container>
              </li>
            } @else if (channelFormTarget() === null) {
              <li class="list-group-item">
                <button type="button" class="btn btn-sm btn-outline-primary" [disabled]="busy()" (click)="openChannelForm(group)">
                  + {{ 'community.admin.channel.add' | translate }}
                </button>
              </li>
            }
          </ul>
        </section>
      }

      <!-- Group form (new / edit) -->
      <ng-template #groupForm>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label small mb-0">{{ 'community.admin.common.name' | translate }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="255" [(ngModel)]="groupDraft.name" />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ 'community.admin.group.icon' | translate }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="64" placeholder="cil-people" [(ngModel)]="groupDraft.icon" />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ 'community.admin.group.visibility' | translate }}</label>
            <select class="form-select form-select-sm" [(ngModel)]="groupDraft.visibility">
              @for (v of groupVisibilities; track v) {
                <option [ngValue]="v">{{ 'community.admin.group_visibility.' + v | translate }}</option>
              }
            </select>
          </div>
          <div class="col-12">
            <label class="form-label small mb-0">{{ 'community.admin.common.description' | translate }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="500" [(ngModel)]="groupDraft.description" />
          </div>
          @if (canManageRoles()) {
            <div class="col-12">
              <label class="form-label small mb-1">{{ 'community.admin.group.roles' | translate }}</label>
              <div class="d-flex flex-wrap gap-3">
                @for (role of activeRoles(); track role.id) {
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" [id]="'grole-' + role.id"
                           [checked]="groupDraft.role_keys.includes(role.key)" (change)="toggleDraftRole(role.key)" />
                    <label class="form-check-label small" [for]="'grole-' + role.id">{{ role.name }}</label>
                  </div>
                }
              </div>
              <div class="form-text">{{ 'community.admin.group.roles_hint' | translate }}</div>
            </div>
          }
          <div class="col-12 d-flex gap-2">
            <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !groupDraft.name.trim()" (click)="submitGroupForm()">
              {{ 'community.admin.common.save' | translate }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelGroupForm()">
              {{ 'community.admin.common.cancel' | translate }}
            </button>
          </div>
        </div>
      </ng-template>

      <!-- Channel form (new / edit) -->
      <ng-template #channelForm>
        <div class="row g-2">
          <div class="col-md-5">
            <label class="form-label small mb-0">{{ 'community.admin.common.name' | translate }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="255" [(ngModel)]="channelDraft.name" />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ 'community.admin.channel.mode' | translate }}</label>
            <select class="form-select form-select-sm" [(ngModel)]="channelDraft.mode">
              @for (mode of channelFormModes(); track mode) {
                <option [ngValue]="mode">{{ 'community.channel.mode.' + mode | translate }}</option>
              }
            </select>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-end">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="chReadOnly" [(ngModel)]="channelDraft.read_only" />
              <label class="form-check-label small" for="chReadOnly">{{ 'community.admin.channel.read_only' | translate }}</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="chApproval" [(ngModel)]="channelDraft.requires_approval" />
              <label class="form-check-label small" for="chApproval">{{ 'community.admin.channel.requires_approval' | translate }}</label>
            </div>
          </div>
          <div class="col-md-5">
            <label class="form-label small mb-0">{{ 'community.admin.channel.site' | translate }}</label>
            <select class="form-select form-select-sm" [(ngModel)]="channelDraft.config_domain_id">
              <option value="">{{ 'community.admin.channel.site_all' | translate }}</option>
              @for (site of sites(); track site.id) {
                <option [value]="site.id">{{ site.name }}</option>
              }
            </select>
            <div class="form-text">{{ 'community.admin.channel.site_hint' | translate }}</div>
          </div>
          <div class="col-md-7">
            <div class="form-check form-switch mt-md-3">
              <input class="form-check-input" type="checkbox" role="switch" id="chPrivate"
                     [checked]="channelDraft.private" [disabled]="channelDraftWasPrivate"
                     (change)="onDraftPrivateChange($any($event.target).checked)" />
              <label class="form-check-label small" for="chPrivate">🔐 {{ 'community.admin.private_room.switch' | translate }}</label>
            </div>
            <div class="form-text">
              @if (channelDraftWasPrivate) {
                {{ 'community.admin.private_room.irreversible' | translate }}
              } @else {
                {{ 'community.admin.private_room.switch_hint' | translate }}
              }
            </div>
          </div>
          <div class="col-12">
            <label class="form-label small mb-0">{{ 'community.admin.common.description' | translate }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="500" [(ngModel)]="channelDraft.description" />
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">{{ 'community.admin.channel.allowed_uploads' | translate }}</label>
            <div class="d-flex flex-wrap gap-3">
              @for (kind of uploadKinds(); track kind) {
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" [id]="'upl-' + kind"
                         [checked]="channelDraft.allowed_uploads.includes(kind)" (change)="toggleDraftList('allowed_uploads', kind)" />
                  <label class="form-check-label small" [for]="'upl-' + kind">{{ 'community.admin.upload_kind.' + kind | translate }}</label>
                </div>
              }
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">{{ 'community.admin.channel.reaction_set' | translate }}</label>
            <div class="d-flex flex-wrap gap-3">
              @for (key of reactionKeys(); track key) {
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" [id]="'react-' + key"
                         [checked]="channelDraft.reaction_set.includes(key)" (change)="toggleDraftList('reaction_set', key)" />
                  <label class="form-check-label small" [for]="'react-' + key">
                    {{ emoji(key) }} {{ 'community.reactions.names.' + key | translate }}
                  </label>
                </div>
              }
            </div>
          </div>
          <div class="col-12 d-flex gap-2">
            <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !channelDraft.name.trim()" (click)="submitChannelForm()">
              {{ 'community.admin.common.save' | translate }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelChannelForm()">
              {{ 'community.admin.common.cancel' | translate }}
            </button>
          </div>
        </div>
      </ng-template>

      <!-- Private Room: participants, read-only mode and (platform admins) the access log -->
      <ng-template #participantsPanel let-ch>
        <div class="community-admin__participants border rounded p-2 mt-2">
          <div class="d-flex align-items-start flex-wrap gap-2 mb-2">
            <div class="form-check form-switch mb-0">
              <input class="form-check-input" type="checkbox" role="switch" [id]="'chPartRo-' + ch.id"
                     [checked]="ch.participants_read_only" [disabled]="busy()"
                     (change)="toggleParticipantsReadOnly(ch)" />
              <label class="form-check-label small" [for]="'chPartRo-' + ch.id">{{ 'community.admin.private_room.read_only_switch' | translate }}</label>
            </div>
            <div class="form-text mt-0 w-100">{{ 'community.admin.private_room.read_only_hint' | translate }}</div>
          </div>

          <h4 class="h6 mb-1">
            {{ 'community.admin.private_room.participants' | translate }}
            <span class="badge bg-light text-dark ms-1">{{ participantsTotal() }}</span>
          </h4>

          @if (participantsError(); as key) {
            <div class="alert alert-danger py-2 small d-flex justify-content-between align-items-start">
              <span>
                {{ key | translate }}
                @if (participantsErrorDetail(); as detail) {
                  <br /><small>{{ detail }}</small>
                }
              </span>
              <button type="button" class="btn-close" (click)="participantsError.set(null)"></button>
            </div>
          }

          @if (participantsLoading() && participants().length === 0) {
            <div class="text-center py-2"><div class="spinner-border spinner-border-sm" role="status"></div></div>
          } @else if (participants().length === 0) {
            <p class="text-muted small mb-2">{{ 'community.admin.private_room.participants_empty' | translate }}</p>
          } @else {
            <div class="table-responsive">
              <table class="table table-sm align-middle small mb-2">
                <thead>
                  <tr>
                    <th>{{ 'community.admin.grants.user' | translate }}</th>
                    <th>{{ 'community.admin.private_room.since' | translate }}</th>
                    <th>{{ 'community.admin.private_room.until' | translate }}</th>
                    <th>{{ 'community.admin.common.reason' | translate }}</th>
                    <th>{{ 'community.admin.private_room.granted_by' | translate }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (p of participants(); track p.id) {
                    <tr>
                      <td>
                        <div>{{ p.user.full_name || '—' }}</div>
                        <small class="text-muted">
                          #{{ p.user.id }}
                          @if (p.user.username) { · {{ p.user.username }} }
                          @if (p.user.email) { · {{ p.user.email }} }
                        </small>
                      </td>
                      <td class="text-nowrap">{{ p.created_at | date:'mediumDate' }}</td>
                      <td class="text-nowrap">{{ p.expires_at ? (p.expires_at | date:'mediumDate') : '—' }}</td>
                      <td>{{ p.reason || '—' }}</td>
                      <td>{{ privateRoomUserLabel(p.granted_by) }}</td>
                      <td class="text-end">
                        <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="participantBusy()"
                                (click)="askRemoveParticipant(ch.id, p)">
                          {{ 'community.admin.private_room.remove' | translate }}
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          <!-- Grant access -->
          <div class="row g-2 align-items-end">
            <div class="col-md-5 position-relative">
              @if (canManageRoles()) {
                <label class="form-label small mb-0">{{ 'community.admin.grants.user_search' | translate }}</label>
                <input type="text" class="form-control form-control-sm" autocomplete="off"
                       [placeholder]="'community.admin.grants.user_search_placeholder' | translate"
                       [ngModel]="participantUserSearchTerm" (ngModelChange)="onParticipantUserSearch($event)" />
                @if (participantUserResults().length > 0) {
                  <ul class="list-group community-admin__user-results">
                    @for (u of participantUserResults(); track u.id) {
                      <li class="list-group-item list-group-item-action py-1 small" role="button" (click)="pickParticipantUser(u)">
                        <strong>#{{ u.id }}</strong> {{ userLabel(u) }}
                      </li>
                    }
                  </ul>
                }
                @if (participantDraft.user; as picked) {
                  <div class="form-text">✓ #{{ picked.id }} {{ userLabel(picked) }}</div>
                }
              } @else {
                <!-- Area admins may not search users (403); they enter the id. -->
                <label class="form-label small mb-0">{{ 'community.admin.grants.user_id' | translate }}</label>
                <input type="number" class="form-control form-control-sm" min="1" [(ngModel)]="participantDraft.user_id" />
              }
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-0">{{ 'community.admin.private_room.add_reason' | translate }} *</label>
              <input type="text" class="form-control form-control-sm" maxlength="500"
                     [placeholder]="'community.admin.common.reason_placeholder' | translate" [(ngModel)]="participantDraft.reason" />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-0">{{ 'community.admin.private_room.add_expires' | translate }}</label>
              <input type="date" class="form-control form-control-sm" [(ngModel)]="participantDraft.expires_at" />
            </div>
            <div class="col-12">
              <button type="button" class="btn btn-sm btn-primary" [disabled]="participantBusy() || !participantDraftValid()" (click)="addParticipant(ch.id)">
                @if (participantBusy()) { <span class="spinner-border spinner-border-sm me-1" role="status"></span> }
                + {{ 'community.admin.private_room.add' | translate }}
              </button>
            </div>
          </div>

          <!-- Accesses by platform admins who are neither participant nor area admin -->
          @if (isPlatformAdmin() && accessLog().length > 0) {
            <h4 class="h6 mt-3 mb-1">{{ 'community.admin.private_room.access_log' | translate }}</h4>
            <ul class="list-unstyled small mb-1 community-admin__access-log">
              @for (entry of accessLog(); track entry.id) {
                <li class="d-flex flex-wrap gap-2 border-bottom py-1">
                  <span class="text-nowrap text-muted">{{ entry.created_at | date:'short' }}</span>
                  <span>{{ privateRoomUserLabel(entry.user) }}</span>
                  <span class="badge bg-light text-dark">{{ entry.action }}</span>
                  @if (entry.ip_address) {
                    <span class="text-muted">{{ entry.ip_address }}</span>
                  }
                </li>
              }
            </ul>
            @if (accessLogTotalPages() > 1) {
              <nav class="d-flex gap-2 align-items-center">
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="accessLogLoading() || accessLogPage() <= 1" (click)="loadAccessLog(ch.id, accessLogPage() - 1)">‹</button>
                <span class="small text-muted">{{ accessLogPage() }} / {{ accessLogTotalPages() }}</span>
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="accessLogLoading() || accessLogPage() >= accessLogTotalPages()" (click)="loadAccessLog(ch.id, accessLogPage() + 1)">›</button>
              </nav>
            }
          }
        </div>
      </ng-template>

      <!-- Public -> private: irreversible, and what was public stays known -->
      @if (privatizeTarget(); as pending) {
        <div class="modal fade show d-block" tabindex="-1" role="dialog" (keydown.escape)="cancelPrivatize()">
          <div class="modal-dialog" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">🔐 {{ 'community.admin.private_room.confirm_title' | translate: { name: pending.name } }}</h5>
                <button type="button" class="btn-close" (click)="cancelPrivatize()"></button>
              </div>
              <div class="modal-body">
                <div class="alert alert-warning mb-0 community-admin__confirm-body">{{ 'community.admin.private_room.confirm_body' | translate }}</div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" (click)="cancelPrivatize()">
                  {{ 'community.admin.common.cancel' | translate }}
                </button>
                <button type="button" class="btn btn-danger" [disabled]="busy()" (click)="confirmPrivatize()">
                  {{ 'community.admin.private_room.confirm_button' | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-backdrop fade show"></div>
      }

      <!-- Withdraw a participant: the reason is mandatory -->
      @if (participantRemoveTarget(); as target) {
        <app-community-reason-modal titleKey="community.admin.private_room.remove"
                                    hintKey="community.admin.private_room.remove_hint"
                                    [titleParams]="{ name: privateRoomUserLabel(target.participant.user) }"
                                    labelKey="community.admin.private_room.remove_reason"
                                    placeholderKey="community.admin.common.reason_placeholder"
                                    confirmKey="community.admin.private_room.remove"
                                    confirmClass="btn-danger"
                                    [busy]="participantBusy()"
                                    [error]="participantRemoveError()"
                                    (confirmed)="confirmRemoveParticipant($event)"
                                    (cancelled)="cancelRemoveParticipant()" />
      }
    }

    <!-- ================================================================== -->
    <!-- Rights matrix                                                        -->
    <!-- ================================================================== -->
    @if (tab() === 'rights') {
      <p class="text-muted small">{{ 'community.admin.rights.hint' | translate }}</p>

      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-4">
          <label class="form-label small mb-0">{{ 'community.admin.rights.group_label' | translate }}</label>
          <select class="form-select form-select-sm" [ngModel]="rightsGroupId()" (ngModelChange)="selectRightsGroup($event)">
            @for (group of groups(); track group.id) {
              <option [ngValue]="group.id">{{ group.name }}</option>
            }
          </select>
        </div>
        @if (addableMatrixRoles().length > 0) {
          <div class="col-md-4">
            <label class="form-label small mb-0">{{ 'community.admin.rights.add_role_row' | translate }}</label>
            <div class="input-group input-group-sm">
              <select class="form-select" [(ngModel)]="addMatrixRoleId">
                <option [ngValue]="null">—</option>
                @for (role of addableMatrixRoles(); track role.id) {
                  <option [ngValue]="role.id">{{ role.name }}</option>
                }
              </select>
              <button type="button" class="btn btn-outline-primary" [disabled]="addMatrixRoleId == null" (click)="addMatrixRole()">+</button>
            </div>
          </div>
        }
      </div>

      @if (rightsGroup(); as group) {
        <!-- Group defaults: one row per role, applies to every channel without an override. -->
        <h2 class="h6">{{ 'community.admin.rights.default_title' | translate: { name: group.name } }}</h2>
        @if (matrixRoles().length === 0) {
          <p class="text-muted small">{{ 'community.admin.rights.no_roles' | translate }}</p>
        } @else {
          <div class="table-responsive mb-4">
            <table class="table table-sm table-bordered community-admin__matrix align-middle">
              <thead>
                <tr>
                  <th class="community-admin__matrix-role">{{ 'community.admin.rights.role' | translate }}</th>
                  @for (flag of flags(); track flag) {
                    <th class="community-admin__matrix-flag" [title]="'community.admin.flags_hint.' + flag | translate">
                      <span>{{ 'community.admin.flags.' + flag | translate }}</span>
                    </th>
                  }
                  <th></th>
                </tr>
              </thead>
              <tbody>
                @for (role of matrixRoles(); track role.id) {
                  <tr [class.table-warning]="isDirty({ group_id: group.id, role_id: role.id })">
                    <th class="community-admin__matrix-role">
                      {{ role.name }}
                      @if (!defaultRow(group, role.id) && !isDirty({ group_id: group.id, role_id: role.id })) {
                        <div class="small text-muted fw-normal">{{ 'community.admin.rights.no_row' | translate }}</div>
                      }
                    </th>
                    @for (flag of flags(); track flag) {
                      <td class="text-center">
                        <input class="form-check-input" type="checkbox"
                               [checked]="rowFlags({ group_id: group.id, role_id: role.id }, defaultRow(group, role.id))[flag]"
                               [disabled]="busy()"
                               (change)="toggleFlag({ group_id: group.id, role_id: role.id }, flag, defaultRow(group, role.id))" />
                      </td>
                    }
                    <td class="text-nowrap">
                      @if (isDirty({ group_id: group.id, role_id: role.id })) {
                        <button type="button" class="btn btn-sm btn-primary me-1" [disabled]="busy()" (click)="saveRow({ group_id: group.id, role_id: role.id }, defaultRow(group, role.id))">
                          {{ 'community.admin.common.save' | translate }}
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="discardRow({ group_id: group.id, role_id: role.id })">
                          {{ 'community.admin.common.cancel' | translate }}
                        </button>
                      } @else if (defaultRow(group, role.id)) {
                        <button type="button" class="btn btn-sm btn-link text-danger" [disabled]="busy()" (click)="removeRow({ group_id: group.id, role_id: role.id })">
                          {{ 'community.admin.rights.remove_default' | translate }}
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Channel overrides -->
          <h2 class="h6">{{ 'community.admin.rights.overrides_title' | translate }}</h2>
          <p class="text-muted small">{{ 'community.admin.rights.overrides_hint' | translate }}</p>
          @for (ch of group.channels; track ch.id) {
            <div class="card mb-2">
              <button type="button" class="card-header btn text-start d-flex align-items-center gap-2 w-100" (click)="toggleChannelExpanded(ch.id)">
                <span>{{ expandedChannels().has(ch.id) ? '▾' : '▸' }}</span>
                <span class="fw-semibold"># {{ ch.name }}</span>
                @if (ch.private) {
                  <span class="badge bg-dark">🔐 {{ 'community.private_room.badge' | translate }}</span>
                }
                @if (ch.permissions.length > 0) {
                  <span class="badge bg-warning text-dark">{{ 'community.admin.rights.override_count' | translate: { count: ch.permissions.length } }}</span>
                }
              </button>
              @if (expandedChannels().has(ch.id)) {
                <div class="table-responsive">
                  <table class="table table-sm table-bordered community-admin__matrix align-middle mb-0">
                    <thead>
                      <tr>
                        <th class="community-admin__matrix-role">{{ 'community.admin.rights.role' | translate }}</th>
                        @for (flag of flags(); track flag) {
                          <th class="community-admin__matrix-flag" [title]="'community.admin.flags_hint.' + flag | translate">
                            <span>{{ 'community.admin.flags.' + flag | translate }}</span>
                          </th>
                        }
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (role of matrixRoles(); track role.id) {
                        @if (overrideRow(ch, role.id) || hasOverrideDraft(ch, role.id)) {
                          <!-- An override row: fully editable, removable. -->
                          <tr [class.table-warning]="isDirty({ channel_id: ch.id, role_id: role.id })">
                            <th class="community-admin__matrix-role">
                              {{ role.name }}
                              <span class="badge bg-warning text-dark ms-1">{{ 'community.admin.rights.override' | translate }}</span>
                            </th>
                            @for (flag of flags(); track flag) {
                              <td class="text-center">
                                <input class="form-check-input" type="checkbox"
                                       [checked]="rowFlags({ channel_id: ch.id, role_id: role.id }, overrideRow(ch, role.id))[flag]"
                                       [disabled]="busy()"
                                       (change)="toggleFlag({ channel_id: ch.id, role_id: role.id }, flag, overrideRow(ch, role.id))" />
                              </td>
                            }
                            <td class="text-nowrap">
                              @if (isDirty({ channel_id: ch.id, role_id: role.id })) {
                                <button type="button" class="btn btn-sm btn-primary me-1" [disabled]="busy()" (click)="saveRow({ channel_id: ch.id, role_id: role.id }, overrideRow(ch, role.id))">
                                  {{ 'community.admin.common.save' | translate }}
                                </button>
                                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="discardRow({ channel_id: ch.id, role_id: role.id })">
                                  {{ 'community.admin.common.cancel' | translate }}
                                </button>
                              } @else {
                                <button type="button" class="btn btn-sm btn-link text-danger" [disabled]="busy()" (click)="removeRow({ channel_id: ch.id, role_id: role.id })">
                                  {{ 'community.admin.rights.remove_override' | translate }}
                                </button>
                              }
                            </td>
                          </tr>
                        } @else {
                          <!-- Inherits the group default: shown muted, one click to override. -->
                          <tr class="community-admin__matrix-inherited">
                            <th class="community-admin__matrix-role">
                              {{ role.name }}
                              <div class="small text-muted fw-normal">{{ 'community.admin.rights.inherits' | translate }}</div>
                            </th>
                            @for (flag of flags(); track flag) {
                              <td class="text-center">
                                <input class="form-check-input" type="checkbox" disabled
                                       [checked]="rowFlags({ group_id: group.id, role_id: role.id }, defaultRow(group, role.id))[flag]" />
                              </td>
                            }
                            <td class="text-nowrap">
                              <button type="button" class="btn btn-sm btn-link" [disabled]="busy()" (click)="startOverride(group, ch, role.id)">
                                {{ 'community.admin.rights.add_override' | translate }}
                              </button>
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          } @empty {
            <p class="text-muted small">{{ 'community.admin.channel.empty' | translate }}</p>
          }
        }
      }
    }

    <!-- ================================================================== -->
    <!-- Roles                                                                -->
    <!-- ================================================================== -->
    @if (tab() === 'roles' && canManageRoles()) {
      <div class="d-flex justify-content-between align-items-center mb-2">
        <p class="text-muted small mb-0">{{ 'community.admin.role.hint' | translate }}</p>
        @if (roleFormTarget() === null) {
          <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="openRoleForm()">
            + {{ 'community.admin.role.add' | translate }}
          </button>
        }
      </div>

      @if (roleFormTarget() !== null) {
        <div class="card mb-3">
          <div class="card-body">
            <h2 class="h6">
              {{ (roleFormTarget() === 'new' ? 'community.admin.role.add' : 'community.admin.role.edit') | translate }}
              @if (editingRole()?.system) {
                <span class="badge bg-dark ms-1">{{ 'community.admin.common.system' | translate }}</span>
              }
            </h2>
            <div class="row g-2">
              <div class="col-md-4">
                <label class="form-label small mb-0">{{ 'community.admin.common.name' | translate }}</label>
                <input type="text" class="form-control form-control-sm" maxlength="255" [(ngModel)]="roleDraft.name" />
              </div>
              <div class="col-md-4">
                <label class="form-label small mb-0">{{ 'community.admin.role.key' | translate }}</label>
                @if (roleFormTarget() === 'new') {
                  <input type="text" class="form-control form-control-sm" maxlength="64" pattern="[a-z0-9_]+"
                         [placeholder]="keyPreview()" [(ngModel)]="roleDraft.key" />
                  <div class="form-text">{{ 'community.admin.role.key_hint' | translate }}</div>
                } @else {
                  <input type="text" class="form-control form-control-sm" [value]="roleDraft.key" readonly />
                }
              </div>
              <div class="col-md-2">
                <label class="form-label small mb-0">{{ 'community.admin.common.position' | translate }}</label>
                <input type="number" class="form-control form-control-sm" min="0" [(ngModel)]="roleDraft.position" />
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="roleActive" [(ngModel)]="roleDraft.active" />
                  <label class="form-check-label small" for="roleActive">{{ 'community.admin.common.active' | translate }}</label>
                </div>
              </div>
              <div class="col-12">
                <label class="form-label small mb-0">{{ 'community.admin.common.description' | translate }}</label>
                <input type="text" class="form-control form-control-sm" maxlength="500" [(ngModel)]="roleDraft.description" />
                <div class="form-text">{{ 'community.admin.role.description_hint' | translate }}</div>
              </div>

              <div class="col-md-3">
                <label class="form-label small mb-0">{{ 'community.admin.role.badge_label' | translate }}</label>
                <input type="text" class="form-control form-control-sm" maxlength="32" [(ngModel)]="roleDraft.badge_label" />
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-0">{{ 'community.admin.role.badge_color' | translate }}</label>
                <input type="text" class="form-control form-control-sm" maxlength="32" list="badgeColors" [(ngModel)]="roleDraft.badge_color" />
                <datalist id="badgeColors">
                  @for (c of badgeColors; track c) {
                    <option [value]="c"></option>
                  }
                </datalist>
                <div class="form-text">{{ 'community.admin.role.badge_color_hint' | translate }}</div>
              </div>
              <div class="col-md-2 d-flex flex-column">
                <span class="form-label small mb-0">{{ 'community.admin.role.badge_preview' | translate }}</span>
                <span class="mt-1">
                  @if (roleDraft.badge_label.trim()) {
                    <span [class]="badgeClass(roleDraft.badge_color)" [style.background-color]="badgeStyle(roleDraft.badge_color)">{{ roleDraft.badge_label }}</span>
                  } @else {
                    <span class="text-muted small">—</span>
                  }
                </span>
              </div>
              <div class="col-md-4">
                <label class="form-label small mb-0">{{ 'community.admin.role.visibility' | translate }}</label>
                <select class="form-select form-select-sm" [(ngModel)]="roleDraft.visibility">
                  @for (v of roleVisibilities(); track v) {
                    <option [ngValue]="v">{{ 'community.admin.visibility.' + v | translate }}</option>
                  }
                </select>
                <div class="form-text">{{ 'community.admin.visibility_hint.' + roleDraft.visibility | translate }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label small mb-0">{{ 'community.admin.role.rule_type' | translate }}</label>
                <select class="form-select form-select-sm" [ngModel]="roleDraft.rule_type" (ngModelChange)="onRuleTypeChange($event)"
                        [disabled]="ruleLocked()">
                  <option value="">{{ 'community.admin.rule.none' | translate }}</option>
                  @for (g of ruleGroups(); track g.group) {
                    <optgroup [label]="'community.admin.rule_group.' + g.group | translate">
                      @for (type of g.types; track type) {
                        <option [value]="type">{{ 'community.admin.rule.' + type | translate }}</option>
                      }
                    </optgroup>
                  }
                </select>
                <div class="form-text">
                  {{ (roleDraft.rule_type ? 'community.admin.rule_hint.' + roleDraft.rule_type : 'community.admin.rule_hint.none') | translate }}
                </div>
              </div>
              @if (ruleNeedsNumber(roleDraft.rule_type)) {
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.role.rule_value' | translate }}</label>
                  <input type="number" class="form-control form-control-sm" min="1" [(ngModel)]="roleDraft.rule_value" />
                </div>
              } @else if (ruleNeedsRank(roleDraft.rule_type)) {
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.role.rule_rank' | translate }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="roleDraft.rule_value">
                    @for (code of rankCodes(); track code) {
                      <option [value]="code">{{ code }}</option>
                    }
                  </select>
                </div>
              }

              <div class="col-12">
                <label class="form-label small mb-1">{{ 'community.admin.role.capabilities' | translate }}</label>
                <div class="d-flex flex-wrap gap-3">
                  @for (cap of capabilities(); track cap) {
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" [id]="'cap-' + cap"
                             [checked]="roleDraft.capabilities.includes(cap)" (change)="toggleDraftCapability(cap)" />
                      <label class="form-check-label small" [for]="'cap-' + cap" [title]="'community.admin.capability_hint.' + cap | translate">
                        {{ 'community.admin.capability.' + cap | translate }}
                      </label>
                    </div>
                  }
                </div>
              </div>

              <div class="col-12 d-flex gap-2">
                <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !roleDraftValid()" (click)="submitRoleForm()">
                  {{ 'community.admin.common.save' | translate }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelRoleForm()">
                  {{ 'community.admin.common.cancel' | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      <div class="table-responsive">
        <table class="table table-sm align-middle">
          <thead>
            <tr>
              <th>{{ 'community.admin.role.badge' | translate }}</th>
              <th>{{ 'community.admin.common.name' | translate }}</th>
              <th>{{ 'community.admin.role.visibility' | translate }}</th>
              <th>{{ 'community.admin.role.rule' | translate }}</th>
              <th>{{ 'community.admin.role.capabilities' | translate }}</th>
              <th class="text-end">{{ 'community.admin.role.assignments' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (role of roles(); track role.id) {
              <tr [class.text-muted]="!role.active">
                <td>
                  @if (role.badge_label) {
                    <span [class]="badgeClass(role.badge_color)" [style.background-color]="badgeStyle(role.badge_color)">{{ role.badge_label }}</span>
                  }
                </td>
                <td>
                  <div class="fw-semibold">
                    {{ role.name }}
                    @if (role.system) {
                      <span class="badge bg-dark ms-1">{{ 'community.admin.common.system' | translate }}</span>
                    }
                    @if (!role.active) {
                      <span class="badge bg-secondary ms-1">{{ 'community.admin.common.inactive' | translate }}</span>
                    }
                  </div>
                  <small class="text-muted">{{ role.key }}</small>
                </td>
                <td class="small">{{ 'community.admin.visibility.' + role.visibility | translate }}</td>
                <td class="small">
                  {{ ruleLabel(role) }}
                  @if (role.automatic) {
                    <span class="badge bg-light text-dark ms-1">{{ 'community.admin.role.automatic' | translate }}</span>
                  }
                </td>
                <td class="small">
                  @for (cap of role.capabilities; track cap) {
                    <span class="badge bg-light text-dark me-1">{{ 'community.admin.capability.' + cap | translate }}</span>
                  }
                </td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm btn-link p-0" (click)="openGrants(role)">{{ role.assignment_count }}</button>
                </td>
                <td class="text-end text-nowrap">
                  <button type="button" class="btn btn-sm btn-outline-secondary me-1" [disabled]="busy()" (click)="openRoleForm(role)">
                    {{ 'community.admin.common.edit' | translate }}
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary me-1" (click)="openGrants(role)">
                    {{ 'community.admin.tabs.grants' | translate }}
                  </button>
                  @if (!role.system) {
                    <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="deleteRole(role)">
                      {{ 'community.admin.common.delete' | translate }}
                    </button>
                  }
                </td>
              </tr>
            } @empty {
              <tr><td colspan="7" class="text-muted">{{ 'community.admin.role.empty' | translate }}</td></tr>
            }
          </tbody>
        </table>
      </div>
    }

    <!-- ================================================================== -->
    <!-- Grants                                                               -->
    <!-- ================================================================== -->
    @if (tab() === 'grants') {
      <p class="text-muted small">{{ 'community.admin.grants.hint' | translate }}</p>

      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-3">
          <label class="form-label small mb-0">{{ 'community.admin.grants.role_label' | translate }}</label>
          <select class="form-select form-select-sm" [ngModel]="grantsRoleId()" (ngModelChange)="selectGrantsRole($event)">
            @for (role of roles(); track role.id) {
              <option [ngValue]="role.id">{{ role.name }}</option>
            }
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label small mb-0" for="grantsSearch">{{ 'community.admin.grants.search' | translate }}</label>
          <div class="input-group input-group-sm">
            <input id="grantsSearch" type="search" class="form-control form-control-sm"
                   [placeholder]="'community.admin.grants.search_placeholder' | translate"
                   [ngModel]="assignmentSearchTerm" (ngModelChange)="onAssignmentSearch($event)" />
            @if (assignmentSearchTerm) {
              <button type="button" class="btn btn-outline-secondary" (click)="clearAssignmentSearch()"
                      [attr.aria-label]="'community.admin.grants.search_clear' | translate">&times;</button>
            }
          </div>
        </div>
        <div class="col-md-2">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="includeRevoked" [checked]="includeRevoked()" (change)="toggleIncludeRevoked()" />
            <label class="form-check-label small" for="includeRevoked">{{ 'community.admin.grants.include_revoked' | translate }}</label>
          </div>
        </div>
        <div class="col-md-3 text-md-end">
          @if (grantsRole() && !grantFormOpen()) {
            <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="openGrantForm()">
              + {{ 'community.admin.grants.grant' | translate }}
            </button>
          }
        </div>
      </div>

      @if (grantFormOpen() && grantsRole(); as role) {
        <div class="card mb-3">
          <div class="card-body">
            <h2 class="h6">{{ 'community.admin.grants.grant_title' | translate: { role: role.name } }}</h2>
            <div class="row g-2">
              <div class="col-md-6 position-relative">
                @if (canManageRoles()) {
                  <label class="form-label small mb-0">{{ 'community.admin.grants.user_search' | translate }}</label>
                  <input type="text" class="form-control form-control-sm" autocomplete="off"
                         [placeholder]="'community.admin.grants.user_search_placeholder' | translate"
                         [ngModel]="userSearchTerm" (ngModelChange)="onUserSearch($event)" />
                  @if (userResults().length > 0) {
                    <ul class="list-group community-admin__user-results">
                      @for (u of userResults(); track u.id) {
                        <li class="list-group-item list-group-item-action py-1 small" role="button" (click)="pickUser(u)">
                          <strong>#{{ u.id }}</strong> {{ userLabel(u) }}
                        </li>
                      }
                    </ul>
                  }
                  @if (grantDraft.user; as picked) {
                    <div class="form-text">✓ #{{ picked.id }} {{ userLabel(picked) }}</div>
                  } @else {
                    <div class="form-text">{{ 'community.admin.grants.user_search_hint' | translate }}</div>
                  }
                } @else {
                  <!-- Area admins may not search users (403); they enter the id. -->
                  <label class="form-label small mb-0">{{ 'community.admin.grants.user_id' | translate }}</label>
                  <input type="number" class="form-control form-control-sm" min="1" [(ngModel)]="grantDraft.user_id" />
                  <div class="form-text">{{ 'community.admin.grants.user_id_hint' | translate }}</div>
                }
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-0">{{ 'community.admin.grants.scope' | translate }}</label>
                <select class="form-select form-select-sm" [ngModel]="grantDraft.scope" (ngModelChange)="onGrantScopeChange($event)">
                  @if (canManageRoles()) {
                    <option value="global">{{ 'community.admin.grants.scope_global' | translate }}</option>
                  }
                  <option value="group">{{ 'community.admin.grants.scope_group' | translate }}</option>
                  <option value="channel">{{ 'community.admin.grants.scope_channel' | translate }}</option>
                </select>
              </div>
              @if (grantDraft.scope === 'group') {
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.grants.scope_group' | translate }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="grantDraft.scope_id">
                    @for (group of groups(); track group.id) {
                      <option [ngValue]="group.id">{{ group.name }}</option>
                    }
                  </select>
                </div>
              } @else if (grantDraft.scope === 'channel') {
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.grants.scope_channel' | translate }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="grantDraft.scope_id">
                    @for (x of allChannels(); track x.channel.id) {
                      <option [ngValue]="x.channel.id">{{ x.group.name }} / # {{ x.channel.name }}</option>
                    }
                  </select>
                </div>
              }
              <div class="col-md-8">
                <label class="form-label small mb-0">{{ 'community.admin.common.reason' | translate }} *</label>
                <input type="text" class="form-control form-control-sm" maxlength="500"
                       [placeholder]="'community.admin.common.reason_placeholder' | translate" [(ngModel)]="grantDraft.reason" />
              </div>
              <div class="col-md-4">
                <label class="form-label small mb-0">{{ 'community.admin.grants.expires_at' | translate }}</label>
                <input type="date" class="form-control form-control-sm" [(ngModel)]="grantDraft.expires_at" />
              </div>
              <div class="col-12 d-flex gap-2">
                <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !grantDraftValid()" (click)="submitGrantForm()">
                  {{ 'community.admin.grants.grant' | translate }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelGrantForm()">
                  {{ 'community.admin.common.cancel' | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      @if (assignmentsLoading()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else if (assignments().length === 0) {
        <p class="text-muted">
          {{ (assignmentSearch() ? 'community.admin.grants.empty_search' : 'community.admin.grants.empty') | translate: { q: assignmentSearch() } }}
        </p>
      } @else {
        <div class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>{{ 'community.admin.grants.user' | translate }}</th>
                <th>{{ 'community.admin.grants.scope' | translate }}</th>
                <th>{{ 'community.admin.grants.source' | translate }}</th>
                <th>{{ 'community.admin.common.reason' | translate }}</th>
                <th>{{ 'community.admin.grants.granted_by' | translate }}</th>
                <th>{{ 'community.admin.grants.dates' | translate }}</th>
                <th>{{ 'community.admin.grants.status' | translate }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (a of assignments(); track a.id) {
                <tr [class.text-muted]="assignmentStatus(a) !== 'active'">
                  <td>
                    <div>{{ userLabel(a.user) }}</div>
                    @if (a.user) {
                      <small class="text-muted">#{{ a.user.id }}</small>
                    }
                  </td>
                  <td class="small">{{ scopeLabel(a) }}</td>
                  <td class="small">{{ 'community.admin.source.' + a.source | translate }}</td>
                  <td class="small">
                    {{ a.reason || '—' }}
                    @if (a.revoke_reason) {
                      <div class="text-danger">{{ 'community.admin.grants.revoke_reason' | translate }}: {{ a.revoke_reason }}</div>
                    }
                  </td>
                  <td class="small">
                    {{ a.granted_by ? userLabel(a.granted_by) : '—' }}
                    @if (a.revoked_by) {
                      <div class="text-danger">{{ 'community.admin.grants.revoked_by' | translate }}: {{ userLabel(a.revoked_by) }}</div>
                    }
                  </td>
                  <td class="small text-nowrap">
                    <div>{{ a.created_at | date:'medium' }}</div>
                    @if (a.expires_at) {
                      <div>{{ 'community.admin.grants.expires_at' | translate }}: {{ a.expires_at | date:'mediumDate' }}</div>
                    }
                    @if (a.revoked_at) {
                      <div class="text-danger">{{ 'community.admin.grants.revoked_at' | translate }}: {{ a.revoked_at | date:'medium' }}</div>
                    }
                  </td>
                  <td>
                    @switch (assignmentStatus(a)) {
                      @case ('active') { <span class="badge bg-success">{{ 'community.admin.grants.status_active' | translate }}</span> }
                      @case ('revoked') { <span class="badge bg-danger">{{ 'community.admin.grants.status_revoked' | translate }}</span> }
                      @default { <span class="badge bg-secondary">{{ 'community.admin.grants.status_expired' | translate }}</span> }
                    }
                  </td>
                  <td class="text-end">
                    @if (assignmentStatus(a) === 'active') {
                      <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="revoke(a)">
                        {{ 'community.admin.grants.revoke' | translate }}
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <small class="text-muted">{{ 'community.admin.grants.total' | translate: { count: assignmentsTotal() } }}</small>
          @if (assignmentsTotalPages() > 1) {
            <nav class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="assignmentsPage() <= 1" (click)="loadAssignments(assignmentsPage() - 1)">‹</button>
              <span class="align-self-center small text-muted">{{ assignmentsPage() }} / {{ assignmentsTotalPages() }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="assignmentsPage() >= assignmentsTotalPages()" (click)="loadAssignments(assignmentsPage() + 1)">›</button>
            </nav>
          }
        </div>
      }
    }

    <!-- ================================================================== -->
    <!-- Communication rules & contact grants                                -->
    <!-- ================================================================== -->
    @if (tab() === 'communication' && canManageRoles()) {
      <p class="text-muted small">{{ 'community.admin.communication.rules_hint' | translate }}</p>

      @if (communicationLoading() && !communication()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else {
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 class="h6 mb-0">{{ 'community.admin.communication.rules_title' | translate }}</h2>
          @if (ruleFormTarget() === null) {
            <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="openRuleForm()">
              + {{ 'community.admin.communication.rule_add' | translate }}
            </button>
          }
        </div>

        @if (ruleFormTarget() !== null) {
          <div class="card mb-3">
            <div class="card-body">
              <h3 class="h6">{{ (ruleFormTarget() === 'new' ? 'community.admin.communication.rule_add' : 'community.admin.communication.rule_edit') | translate }}</h3>
              <div class="row g-2">
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.communication.from_role' | translate }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="ruleDraft.from_role_id">
                    <option [ngValue]="null">{{ 'community.admin.communication.any_role' | translate }}</option>
                    @for (r of roles(); track r.id) {
                      <option [ngValue]="r.id">{{ r.name }}</option>
                    }
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.communication.to_role' | translate }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="ruleDraft.to_role_id">
                    <option [ngValue]="null">{{ 'community.admin.communication.any_role' | translate }}</option>
                    @for (r of roles(); track r.id) {
                      <option [ngValue]="r.id">{{ r.name }}</option>
                    }
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label small mb-0">{{ 'community.admin.communication.mode_label' | translate }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="ruleDraft.mode">
                    @for (m of communicationModes(); track m) {
                      <option [value]="m">{{ ruleModeLabel(m) }}</option>
                    }
                  </select>
                  <div class="form-text">{{ 'community.admin.communication.mode_hint.' + ruleDraft.mode | translate }}</div>
                </div>
                @if (ruleDraft.mode === 'sponsor_line') {
                  <div class="col-md-3">
                    <label class="form-label small mb-0">{{ 'community.admin.communication.depth' | translate }}</label>
                    <input type="number" class="form-control form-control-sm" min="1" max="10" [(ngModel)]="ruleDraft.depth" />
                  </div>
                }
                <div class="col-md-8">
                  <label class="form-label small mb-0">{{ 'community.admin.common.description' | translate }}</label>
                  <input type="text" class="form-control form-control-sm" maxlength="255" [(ngModel)]="ruleDraft.description" />
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="ruleActive" [(ngModel)]="ruleDraft.active" />
                    <label class="form-check-label" for="ruleActive">{{ 'community.admin.common.active' | translate }}</label>
                  </div>
                </div>
                <div class="col-12 d-flex gap-2">
                  <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="submitRuleForm()">
                    {{ 'community.admin.common.save' | translate }}
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="cancelRuleForm()">
                    {{ 'community.admin.common.cancel' | translate }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        }

        @if (rules().length === 0) {
          <p class="text-muted">{{ 'community.admin.communication.rules_empty' | translate }}</p>
        } @else {
          <div class="table-responsive mb-4">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th>{{ 'community.admin.communication.from_role' | translate }}</th>
                  <th>{{ 'community.admin.communication.to_role' | translate }}</th>
                  <th>{{ 'community.admin.communication.mode_label' | translate }}</th>
                  <th>{{ 'community.admin.common.description' | translate }}</th>
                  <th>{{ 'community.admin.common.active' | translate }}</th>
                  <th class="text-end"></th>
                </tr>
              </thead>
              <tbody>
                @for (r of rules(); track r.id) {
                  <tr [class.text-muted]="!r.active">
                    <td class="small">{{ ruleRoleLabel(r.from_role_key) }}</td>
                    <td class="small">{{ ruleRoleLabel(r.to_role_key) }}</td>
                    <td class="small">{{ ruleModeLabel(r.mode) }}{{ r.mode === 'sponsor_line' ? ' (' + r.depth + ')' : '' }}</td>
                    <td class="small">{{ r.description || '—' }}</td>
                    <td>
                      @if (r.active) {
                        <span class="badge bg-success">{{ 'community.admin.common.active' | translate }}</span>
                      } @else {
                        <span class="badge bg-secondary">{{ 'community.admin.common.inactive' | translate }}</span>
                      }
                    </td>
                    <td class="text-end text-nowrap">
                      <button type="button" class="btn btn-sm btn-link" [disabled]="busy()" (click)="moveRule(r, -1)">↑</button>
                      <button type="button" class="btn btn-sm btn-link" [disabled]="busy()" (click)="moveRule(r, 1)">↓</button>
                      <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="openRuleForm(r)">
                        {{ 'community.admin.common.edit' | translate }}
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="toggleRuleActive(r)">
                        {{ (r.active ? 'community.admin.common.archive' : 'community.admin.common.unarchive') | translate }}
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="deleteRule(r)">
                        {{ 'community.admin.common.delete' | translate }}
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }

        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 class="h6 mb-0">{{ 'community.admin.communication.grants_title' | translate }}</h2>
          @if (!contactGrantFormOpen()) {
            <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="openContactGrantForm()">
              + {{ 'community.admin.communication.grant_add' | translate }}
            </button>
          }
        </div>
        <p class="text-muted small">{{ 'community.admin.communication.grants_hint' | translate }}</p>

        @if (contactGrantFormOpen()) {
          <div class="card mb-3">
            <div class="card-body">
              <div class="row g-2">
                <div class="col-md-6 position-relative">
                  <label class="form-label small mb-0">{{ 'community.admin.communication.from_user' | translate }}</label>
                  <input type="text" class="form-control form-control-sm" autocomplete="off"
                         [placeholder]="'community.admin.grants.user_search_placeholder' | translate"
                         [ngModel]="contactUserSearchTerm" (ngModelChange)="onContactUserSearch($event)" />
                  @if (contactUserResults().length > 0) {
                    <ul class="list-group community-admin__user-results">
                      @for (u of contactUserResults(); track u.id) {
                        <li class="list-group-item list-group-item-action py-1 small" role="button" (click)="pickContactUser(u)">
                          <strong>#{{ u.id }}</strong> {{ userLabel(u) }}
                        </li>
                      }
                    </ul>
                  }
                  @if (contactGrantDraft.user; as picked) {
                    <div class="form-text">✓ #{{ picked.id }} {{ userLabel(picked) }}</div>
                  }
                </div>
                <div class="col-md-6 position-relative">
                  <label class="form-label small mb-0">{{ 'community.admin.communication.to_contact' | translate }}</label>
                  <input type="text" class="form-control form-control-sm" autocomplete="off"
                         [placeholder]="'community.admin.grants.user_search_placeholder' | translate"
                         [ngModel]="contactContactSearchTerm" (ngModelChange)="onContactContactSearch($event)" />
                  @if (contactContactResults().length > 0) {
                    <ul class="list-group community-admin__user-results">
                      @for (u of contactContactResults(); track u.id) {
                        <li class="list-group-item list-group-item-action py-1 small" role="button" (click)="pickContact(u)">
                          <strong>#{{ u.id }}</strong> {{ userLabel(u) }}
                        </li>
                      }
                    </ul>
                  }
                  @if (contactGrantDraft.contact; as picked) {
                    <div class="form-text">✓ #{{ picked.id }} {{ userLabel(picked) }}</div>
                  }
                </div>
                <div class="col-md-8">
                  <label class="form-label small mb-0">{{ 'community.admin.common.reason' | translate }} *</label>
                  <input type="text" class="form-control form-control-sm" maxlength="500"
                         [placeholder]="'community.admin.common.reason_placeholder' | translate" [(ngModel)]="contactGrantDraft.reason" />
                </div>
                <div class="col-md-4">
                  <label class="form-label small mb-0">{{ 'community.admin.grants.expires_at' | translate }}</label>
                  <input type="date" class="form-control form-control-sm" [(ngModel)]="contactGrantDraft.expires_at" />
                </div>
                <div class="col-12">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="bothWays" [(ngModel)]="contactGrantDraft.both_ways" />
                    <label class="form-check-label" for="bothWays">{{ 'community.admin.communication.both_ways' | translate }}</label>
                  </div>
                </div>
                <div class="col-12 d-flex gap-2">
                  <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !contactGrantDraftValid()" (click)="submitContactGrantForm()">
                    {{ 'community.admin.communication.grant_add' | translate }}
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="cancelContactGrantForm()">
                    {{ 'community.admin.common.cancel' | translate }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        }

        @if (contactGrants().length === 0) {
          <p class="text-muted">{{ 'community.admin.communication.grants_empty' | translate }}</p>
        } @else {
          <div class="table-responsive">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th>{{ 'community.admin.communication.from_user' | translate }}</th>
                  <th>{{ 'community.admin.communication.to_contact' | translate }}</th>
                  <th>{{ 'community.admin.common.reason' | translate }}</th>
                  <th>{{ 'community.admin.grants.granted_by' | translate }}</th>
                  <th>{{ 'community.admin.grants.dates' | translate }}</th>
                  <th class="text-end"></th>
                </tr>
              </thead>
              <tbody>
                @for (g of contactGrants(); track g.id) {
                  <tr>
                    <td class="small">{{ userLabel(g.user) }}</td>
                    <td class="small">{{ userLabel(g.contact) }}</td>
                    <td class="small">{{ g.reason || '—' }}</td>
                    <td class="small">{{ g.granted_by ? userLabel(g.granted_by) : '—' }}</td>
                    <td class="small text-nowrap">
                      <div>{{ g.created_at | date:'medium' }}</div>
                      @if (g.expires_at) {
                        <div>{{ 'community.admin.grants.expires_at' | translate }}: {{ g.expires_at | date:'mediumDate' }}</div>
                      }
                    </td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="revokeContactGrant(g)">
                        {{ 'community.admin.grants.revoke' | translate }}
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
    }

    <!-- ================================================================== -->
    <!-- Statistics                                                           -->
    <!-- ================================================================== -->
    @if (tab() === 'stats') {
      <div class="d-flex justify-content-between align-items-center mb-3">
        <p class="text-muted small mb-0">{{ 'community.admin.stats.hint' | translate }}</p>
        <div class="d-flex align-items-center gap-2">
          <label class="small mb-0">{{ 'community.admin.stats.period_label' | translate }}</label>
          <select class="form-select form-select-sm w-auto" [ngModel]="statsDays()" (ngModelChange)="selectStatsDays($event)">
            <option [ngValue]="7">{{ 'community.admin.stats.period_7' | translate }}</option>
            <option [ngValue]="30">{{ 'community.admin.stats.period_30' | translate }}</option>
            <option [ngValue]="90">{{ 'community.admin.stats.period_90' | translate }}</option>
          </select>
        </div>
      </div>

      @if (statsLoading() && !stats()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else if (stats(); as s) {
        <div class="row g-3 mb-4">
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0">{{ s.members_total }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.members_total' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0">{{ s.active_users_period }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.active_users' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0">{{ s.writers_period }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.writers' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0">{{ s.threads_period }} / {{ s.posts_period }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.threads_posts_period' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0">{{ s.threads_total }} / {{ s.posts_total }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.threads_posts_total' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0">{{ s.reactions_total }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.reactions_total' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0 text-warning">{{ s.pending_approval }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.pending_approval' | translate }}</div>
            </div></div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center"><div class="card-body py-3">
              <div class="h4 mb-0" [class.text-danger]="s.reports_open > 0">{{ s.reports_open }}</div>
              <div class="small text-muted">{{ 'community.admin.stats.reports_open' | translate }}</div>
            </div></div>
          </div>
        </div>

        @if (s.role_holders.length > 0) {
          <h2 class="h6">{{ 'community.admin.stats.role_holders_title' | translate }}</h2>
          <div class="d-flex flex-wrap gap-2 mb-4">
            @for (r of s.role_holders; track r.key) {
              <span class="badge bg-light text-dark">{{ r.name }}: {{ r.count }}</span>
            }
          </div>
        }

        <h2 class="h6">{{ 'community.admin.stats.channels_title' | translate }}</h2>
        <div class="table-responsive mb-4">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>{{ 'community.admin.stats.channel' | translate }}</th>
                <th class="text-end">{{ 'community.admin.stats.threads_posts_period' | translate }}</th>
                <th class="text-end">{{ 'community.admin.stats.threads_posts_total' | translate }}</th>
                <th class="text-end">{{ 'community.admin.stats.active_users' | translate }}</th>
                <th class="text-end">{{ 'community.admin.stats.subscribers' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (c of s.channels; track c.channel_id) {
                <tr>
                  <td># {{ c.channel }} <small class="text-muted">({{ c.group }})</small></td>
                  <td class="text-end">{{ c.threads_period }} / {{ c.posts_period }}</td>
                  <td class="text-end">{{ c.threads_total }} / {{ c.posts_total }}</td>
                  <td class="text-end">{{ c.active_users_period }}</td>
                  <td class="text-end">{{ c.subscribers }}</td>
                </tr>
              } @empty {
                <tr><td colspan="5" class="text-muted">{{ 'community.admin.channel.empty' | translate }}</td></tr>
              }
            </tbody>
          </table>
        </div>

        <h2 class="h6">{{ 'community.admin.stats.weekly_title' | translate }}</h2>
        <ul class="list-unstyled community-admin__weekly">
          @for (w of s.weekly; track w.week_start) {
            <li class="mb-2">
              <div class="d-flex justify-content-between small mb-1">
                <span>{{ w.week_start | date:'mediumDate' }}</span>
                <span class="text-muted">{{ 'community.admin.stats.weekly_summary' | translate: { threads: w.threads, posts: w.posts, reactions: w.reactions } }}</span>
              </div>
              <div class="progress" style="height: 6px;" [title]="'community.admin.stats.weekly_posts_bar' | translate">
                <div class="progress-bar" [style.width.%]="weekBarWidth(w.posts, 'posts')"></div>
              </div>
            </li>
          } @empty {
            <li class="text-muted small">{{ 'community.admin.stats.weekly_empty' | translate }}</li>
          }
        </ul>
      }
    }

    <!-- ================================================================== -->
    <!-- Webhooks (manage_roles only)                                        -->
    <!-- ================================================================== -->
    @if (tab() === 'webhooks' && canManageRoles()) {
      <div class="d-flex justify-content-between align-items-center mb-2">
        <p class="text-muted small mb-0">{{ 'community.admin.webhooks.hint' | translate }}</p>
        @if (webhookFormTarget() === null) {
          <button type="button" class="btn btn-sm btn-primary" [disabled]="busy()" (click)="openWebhookForm()">
            + {{ 'community.admin.webhooks.add' | translate }}
          </button>
        }
      </div>

      @if (webhookSecret(); as secret) {
        <div class="alert alert-warning">
          <p class="mb-1">{{ 'community.admin.webhooks.secret_notice' | translate }}</p>
          <div class="input-group input-group-sm">
            <input type="text" class="form-control" [value]="secret" readonly onclick="this.select()" />
          </div>
        </div>
      }

      @if (webhookFormTarget() !== null) {
        <div class="card mb-3">
          <div class="card-body">
            <h2 class="h6">{{ (webhookFormTarget() === 'new' ? 'community.admin.webhooks.add' : 'community.admin.webhooks.edit') | translate }}</h2>
            <div class="row g-2">
              <div class="col-md-4">
                <label class="form-label small mb-0">{{ 'community.admin.common.name' | translate }}</label>
                <input type="text" class="form-control form-control-sm" maxlength="255" [(ngModel)]="webhookDraft.name" />
              </div>
              <div class="col-md-8">
                <label class="form-label small mb-0">{{ 'community.admin.webhooks.url' | translate }}</label>
                <input type="url" class="form-control form-control-sm" placeholder="https://" [(ngModel)]="webhookDraft.url" />
              </div>
              <div class="col-12">
                <label class="form-label small mb-1">{{ 'community.admin.webhooks.events' | translate }}</label>
                <div class="d-flex flex-wrap gap-3">
                  @for (event of webhookEvents(); track event) {
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" [id]="'ev-' + event"
                             [checked]="webhookDraft.events.includes(event)" (change)="toggleWebhookEvent(event)" />
                      <label class="form-check-label small" [for]="'ev-' + event">{{ 'community.admin.webhooks.event.' + event | translate }}</label>
                    </div>
                  }
                </div>
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="webhookActive" [(ngModel)]="webhookDraft.active" />
                  <label class="form-check-label small" for="webhookActive">{{ 'community.admin.common.active' | translate }}</label>
                </div>
              </div>
              @if (webhookFormTarget() !== 'new') {
                <div class="col-md-8 d-flex align-items-end">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="rotateSecret" [(ngModel)]="webhookDraft.rotate_secret" />
                    <label class="form-check-label small" for="rotateSecret">{{ 'community.admin.webhooks.rotate_secret' | translate }}</label>
                  </div>
                </div>
              }
              <div class="col-12 d-flex gap-2">
                <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !webhookDraftValid()" (click)="submitWebhookForm()">
                  {{ 'community.admin.common.save' | translate }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelWebhookForm()">
                  {{ 'community.admin.common.cancel' | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      @if (webhooksLoading() && !webhooksLoaded()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else if (webhooks().length === 0) {
        <p class="text-muted">{{ 'community.admin.webhooks.empty' | translate }}</p>
      } @else {
        @for (w of webhooks(); track w.id) {
          <div class="card mb-3" [class.text-muted]="!w.active">
            <div class="card-header d-flex align-items-center flex-wrap gap-2">
              <span class="fw-semibold">{{ w.name }}</span>
              @if (w.active) {
                <span class="badge bg-success">{{ 'community.admin.common.active' | translate }}</span>
              } @else {
                <span class="badge bg-secondary">{{ 'community.admin.common.inactive' | translate }}</span>
              }
              @if (w.last_error) {
                <span class="badge bg-danger" [title]="w.last_error">{{ 'community.admin.webhooks.last_error' | translate }}</span>
              }
              <span class="ms-auto d-flex flex-wrap gap-1">
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="testWebhook(w)">
                  {{ 'community.admin.webhooks.test' | translate }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="openWebhookForm(w)">
                  {{ 'community.admin.common.edit' | translate }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-dark" [disabled]="busy()" (click)="toggleWebhookActive(w)">
                  {{ (w.active ? 'community.admin.common.archive' : 'community.admin.common.unarchive') | translate }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="deleteWebhook(w)">
                  {{ 'community.admin.common.delete' | translate }}
                </button>
              </span>
            </div>
            <div class="card-body py-2">
              <div class="small text-muted mb-1">{{ w.url }}</div>
              <div class="d-flex flex-wrap gap-1 mb-2">
                @for (event of w.events; track event) {
                  <span class="badge bg-light text-dark">{{ 'community.admin.webhooks.event.' + event | translate }}</span>
                }
              </div>
              <div class="small text-muted">
                {{ 'community.admin.webhooks.last_success' | translate }}: {{ w.last_success_at ? (w.last_success_at | date:'medium') : '—' }}
                · {{ 'community.admin.webhooks.last_failure' | translate }}: {{ w.last_failure_at ? (w.last_failure_at | date:'medium') : '—' }}
              </div>

              @if (deliveriesFor(w.id).length > 0) {
                <div class="table-responsive mt-2">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>{{ 'community.admin.webhooks.event_label' | translate }}</th>
                        <th>{{ 'community.admin.webhooks.status' | translate }}</th>
                        <th>{{ 'community.admin.webhooks.attempts' | translate }}</th>
                        <th>{{ 'community.admin.webhooks.response_code' | translate }}</th>
                        <th>{{ 'community.admin.grants.dates' | translate }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (d of deliveriesFor(w.id); track d.id) {
                        <tr>
                          <td class="small">{{ 'community.admin.webhooks.event.' + d.event | translate }}</td>
                          <td class="small">{{ 'community.admin.webhooks.delivery_status.' + d.status | translate }}</td>
                          <td class="small">{{ d.attempts }}</td>
                          <td class="small">{{ d.response_code ?? '—' }}</td>
                          <td class="small text-nowrap">{{ (d.delivered_at || d.created_at) | date:'medium' }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        }
      }
    }
  }
</div>
